import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { getDashboardsApi, setDashboardDefaultApi } from "src/api/dashboardApi";
import useBuilderStore from "src/store/builderStore";
import type { DashboardListResponse } from "src/types/dashboard";

/**
 * Owns the builder header's dashboard-list query plus which dashboard is
 * selected in the builder. Deliberately not shared with the analytics
 * header's useDefaultDashboard: this one only fills in the server-flagged
 * default when nothing is selected yet (no fallback to the first dashboard,
 * no reset back to empty on unmount), since the builder's selection should
 * persist rather than reset every time this header remounts.
 */
export function useBuilderDefaultDashboard(enabled: boolean) {
  const { selectedDashboardCode, setSelectedDashboardCode } =
    useBuilderStore();
  const queryClient = useQueryClient();

  const { data: allDashboards } = useQuery<DashboardListResponse, Error>({
    queryKey: ["dashboards"],
    queryFn: () => getDashboardsApi(),
    enabled,
    staleTime: 0,
  });

  // Typed as DashboardListItem[] | null — the backend sends null (not []) for
  // a user with no personal dashboards. Normalized to [] here so "My
  // Dashboards" always renders with its "No dashboards found" placeholder,
  // instead of the dropdown itself disappearing whenever the raw value was
  // null (only an empty array used to still show it, empty).
  const myDashboards = allDashboards?.my_dashboards ?? [];

  useEffect(() => {
    const foundDashboard = allDashboards?.dashboards?.find(
      (dash) => dash.is_default === true,
    );

    if (foundDashboard && !selectedDashboardCode) {
      setSelectedDashboardCode(foundDashboard.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDashboards]);

  const { mutate: setDefault } = useMutation({
    mutationFn: (code: string) => setDashboardDefaultApi(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      toast.success("Default dashboard updated.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to set default dashboard.");
    },
  });

  return {
    allDashboards,
    myDashboards,
    selectedDashboardCode,
    setSelectedDashboardCode,
    setDefault,
  };
}
